import { Button, PageHeader, Tooltip } from 'antd';
import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';

import './SDetailLayout.scss';

export const SDetailLayout: FC<Props> = ({
  title,
  backRoute,
  mode,
  onEdit,
  onSave,
  onCancel,
  reset,
  isDirty,
  disabled = false,
  saveDisabled = false,
  headerMenu,
  headerExtra,
  isValid,
  children,
  tooltipEdit,
  extraViewFooter,
  buttonSaveText,
  customFooterButtons,
  isLoading = false,
  hasFooter = true,
  className,
}) => {
  const history = useHistory();
  const [isButtonLoading, setIsButtonLoading] = useState(isLoading);
  const { enterViewMode } = useContext(ViewEditModeContext);

  const cancelCallback = () => {
    if (reset) {
      reset();
    }

    if (onCancel) {
      onCancel();
    }
  };

  const onClickSave = () => {
    if (isValid) {
      setIsButtonLoading(true);
    }

    if (onSave) {
      onSave();
    }
  };

  const onClickEdit = () => {
    setIsButtonLoading(false);

    if (onEdit) {
      onEdit();
    }
  };

  useEffect(() => {
    if (!isValid) {
      setIsButtonLoading(false);
    }
  }, [isValid]);

  useEffect(() => {
    if (!isLoading) {
      setIsButtonLoading(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      setIsButtonLoading(false);
    }
  }, [isLoading]);

  return (
    <div className={classNames('detail-layout', className)}>
      {title && (
        <PageHeader
          className={`detail-layout__header ${
            headerMenu ? 'detail-layout__header-menu' : ''
          }`}
          onBack={() => {
            enterViewMode();
            backRoute ? history.push(backRoute) : history.goBack();
          }}
          title={title}
          extra={headerExtra}
        >
          {headerMenu}
        </PageHeader>
      )}
      {children}
      {hasFooter && (
        <div className="detail-layout__footer">
          {customFooterButtons ? (
            customFooterButtons
          ) : mode === 'edit' ? (
            <>
              {onCancel && (
                <Button
                  size="large"
                  disabled={disabled}
                  onClick={cancelCallback}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="primary"
                size="large"
                loading={isButtonLoading}
                disabled={
                  !isDirty || disabled || isButtonLoading || saveDisabled
                }
                onClick={onClickSave}
              >
                {buttonSaveText ?? 'Save'}
              </Button>
            </>
          ) : tooltipEdit ? (
            <>
              {extraViewFooter}
              <Tooltip title={tooltipEdit}>
                <Button size="large" disabled={disabled} onClick={onClickEdit}>
                  {buttonSaveText ?? 'Edit'}
                </Button>
              </Tooltip>
            </>
          ) : (
            <>
              {extraViewFooter}
              <Button size="large" disabled={disabled} onClick={onClickEdit}>
                {buttonSaveText ?? 'Edit'}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

type Props = {
  title?: string | ReactNode;
  backRoute?: string;
  mode: string;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  reset?: () => void;
  isDirty: boolean;
  disabled?: boolean;
  saveDisabled?: boolean;
  headerMenu?: ReactNode;
  headerExtra?: ReactNode;
  isValid?: boolean;
  children?: ReactNode;
  tooltipEdit?: React.ReactNode;
  extraViewFooter?: ReactNode;
  buttonSaveText?: string;
  customFooterButtons?: ReactNode;
  isLoading?: boolean;
  hasFooter?: boolean;
  className?: string;
};

export type ExternalProps = Omit<Props, 'history'>;
